import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft, ArrowRight, Clock, Target, BarChart3, Lock, Star } from "lucide-react";
import SEO from "@/components/SEO";

const QuizSEO = ({ mode }) => (
  <SEO
    path={mode === "full" ? "/checkup-completo" : "/checkup"}
    title={mode === "full"
      ? "Check-Up Aziendale Completo — Analisi approfondita in 42 domande"
      : "Check-Up Aziendale — Diagnosi gratuita in pochi minuti"}
    description={mode === "full"
      ? "Check-Up Aziendale completo basato sul Metodo A.L.L.I.N.E.A.: 42 domande su 7 pilastri, report .docx personalizzato consegnato via email. Diagnosi strategica approfondita della tua azienda."
      : "Check-Up Aziendale gratuito basato sul Metodo A.L.L.I.N.E.A.: 9 domande chiave sui 7 pilastri della tua impresa, pochi minuti di tempo, diagnosi personalizzata consegnata via email."}
    noindex={mode === "full"}
  />
);

// Regex validazione (email standard + telefono con 8-15 cifre, prefisso opzionale)
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^(\+?\d[\d\s.\-()]{7,20}\d)$/;

/**
 * Genera un breve suono di completamento elegante con Web Audio API.
 * Due note ascendenti (accordo brand: G#4 → E5) con soft attack/release,
 * onda triangolare per un timbro caldo e non invadente.
 */
function playCompletionChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    const notes = [
      { freq: 415.30, start: 0.00, dur: 0.55 }, // G#4
      { freq: 659.25, start: 0.16, dur: 0.85 }, // E5
    ];
    const master = ctx.createGain();
    master.gain.value = 0.18;
    master.connect(ctx.destination);
    notes.forEach(n => {
      const osc = ctx.createOscillator();
      osc.type = "triangle";
      osc.frequency.value = n.freq;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, now + n.start);
      g.gain.exponentialRampToValueAtTime(0.9, now + n.start + 0.04);
      g.gain.exponentialRampToValueAtTime(0.0001, now + n.start + n.dur);
      osc.connect(g); g.connect(master);
      osc.start(now + n.start);
      osc.stop(now + n.start + n.dur + 0.05);
    });
    setTimeout(() => ctx.close && ctx.close(), 1500);
  } catch (_) { /* silente */ }
}

/**
 * Suono singolo delicato (nota G4) per il checkpoint di pilastro.
 * Più leggero del chime finale, per creare senso di ritmo senza fastidio.
 */
function playPillarTick() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = 523.25; // C5
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.35, now + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
    osc.connect(g); g.connect(ctx.destination);
    osc.start(now); osc.stop(now + 0.55);
    setTimeout(() => ctx.close && ctx.close(), 900);
  } catch (_) {}
}

const ROLES = [
  "Titolare / Fondatore / CEO",
  "Amministratore / Direttore Generale",
  "Responsabile di Reparto (Operations/Marketing/HR)",
  "Libero Professionista / Consulente",
];
const COLLABORATORS = [
  "Solo io / Gestione autonoma",
  "Da 2 a 5 collaboratori",
  "Da 6 a 15 collaboratori",
  "Oltre 15 collaboratori",
];

function ThankYou() {
  // Meta Pixel — evento standard "Lead" + suono elegante di completamento.
  // Il componente viene montato SOLO dopo che il backend ha risposto 200 OK
  // (setPhase("done") è chiamato in submitAll dopo l'await della POST).
  // sessionStorage previene doppi invii (StrictMode / re-mount).
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem("rc_lead_fired")) return;
    if (typeof window.fbq === "function") window.fbq("track", "Lead");
    playCompletionChime();
    sessionStorage.setItem("rc_lead_fired", "1");
  }, []);

  return (
    <section className="section-pad">
      <div className="max-w-3xl mx-auto px-6" data-testid="quiz-thankyou">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#323C36] mb-6 relative">
            <CheckCircle2 className="text-[#FAC075]" size={40} strokeWidth={1.6}/>
            <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[#FAC075] flex items-center justify-center">
              <span className="text-[#2A3229] text-xs font-bold">✓</span>
            </span>
          </div>
          <p className="eyebrow mb-3">Analisi completata con successo</p>
          <h1 className="font-display text-4xl sm:text-5xl text-[#2A3229] leading-tight">
            Il tuo <span className="text-[#323C36]">Check‑Up</span>{" "}
            <span className="text-[#A8674F]">A.L.L.I.N.E.A.</span> è in analisi
          </h1>
        </div>

        <div className="bg-white border border-[#323C36]/12 p-8 sm:p-12 mb-8">
          <div className="space-y-5 text-[#2A3229] leading-relaxed text-base sm:text-lg">
            <p>Le tue risposte sono state ricevute correttamente. Sto ora analizzando personalmente i tuoi dati per costruire un report dettagliato e delineare le strade più efficaci per la tua impresa.</p>
            <p className="pl-4 border-l-2 border-[#A8674F]">
              <strong className="text-[#323C36]">Entro 24 ore lavorative</strong> riceverai una email con il tuo report personalizzato e, subito dopo, ti contatterò per approfondire insieme le opportunità di intervento.
            </p>
          </div>
          <div className="mt-8 pt-6 border-t border-[#323C36]/10">
            <p className="font-display text-lg text-[#323C36]">Roberta Castagna</p>
            <p className="text-xs uppercase tracking-[0.24em] text-[#A8674F] font-semibold mt-1">Consulente d'Impresa</p>
          </div>
        </div>

        <div className="bg-[#EDE7DD] p-8 sm:p-10 text-center">
          <p className="eyebrow mb-3">Vuoi accelerare i tempi?</p>
          <h2 className="font-display text-2xl sm:text-3xl text-[#2A3229] leading-tight mb-3">
            Prenota una call strategica di 30 minuti
          </h2>
          <p className="text-[#5A6058] leading-relaxed mb-6 max-w-lg mx-auto">
            Ci confrontiamo direttamente sui punti emersi dal tuo Check‑Up e delineiamo insieme le priorità di intervento.
          </p>
          <a data-testid="thankyou-book" href="https://calendar.app.google/wMsDARnKAyyi4doX6" target="_blank" rel="noreferrer" className="btn-accent">
            Prenota la tua call →
          </a>
        </div>

        <div className="mt-10 text-center">
          <a href="/" className="btn-secondary">← Torna alla home</a>
        </div>
      </div>
    </section>
  );
}

function WelcomeScreen({ onStart, onResume, savedProgress, mode, storageKey, totalQ }) {
  const [company, setCompany] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (!company.trim()) return;
    onStart(company.trim());
  };
  return (
    <section className="pt-16 pb-12 sm:pt-32 sm:pb-24">
      <div className="max-w-2xl mx-auto px-6" data-testid="quiz-welcome">
        <p className="eyebrow mb-2.5">Benvenuto</p>
        <h1 className="font-display text-2xl sm:text-5xl text-[#2A3229] leading-tight mb-4">
          Benvenuto nel <span className="text-[#323C36]">Check‑Up</span> <span className="text-[#A8674F]">A.L.L.I.N.E.A.</span>
        </h1>
        <p className="text-[#5A6058] text-sm sm:text-lg leading-relaxed mb-5">
          Rispondi alle domande per analizzare lo stato dei tuoi processi interni e del tuo brand.
        </p>

        {/* VALUE BULLETS */}
        <ul className="space-y-2.5 mb-6" data-testid="value-bullets">
          <li className="flex items-start gap-3">
            <span className="mt-0.5 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-[#A8674F]/10 text-[#A8674F] flex items-center justify-center shrink-0"><Target size={13}/></span>
            <span className="text-[#2A3229] text-sm sm:text-base leading-snug">Individua i <strong>colli di bottiglia operativi</strong> della tua azienda</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="mt-0.5 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-[#A8674F]/10 text-[#A8674F] flex items-center justify-center shrink-0"><BarChart3 size={13}/></span>
            <span className="text-[#2A3229] text-sm sm:text-base leading-snug">Ricevi una <strong>diagnosi personalizzata</strong> sullo stato del tuo brand</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="mt-0.5 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-[#A8674F]/10 text-[#A8674F] flex items-center justify-center shrink-0"><Clock size={13}/></span>
            <span className="text-[#2A3229] text-sm sm:text-base leading-snug">{mode === "full"
              ? <>Analisi completa in <strong>42 domande</strong> — diagnosi personalizzata via email</>
              : <>Richiede solo <strong>pochi minuti</strong> — diagnosi personalizzata via email</>}</span>
          </li>
        </ul>

        {savedProgress && (
          <div
            data-testid="resume-banner"
            className="bg-[#F6E6D8] border-l-4 border-[#A8674F] p-5 sm:p-6 mb-8 flex flex-col sm:flex-row sm:items-center gap-4"
          >
            <div className="flex-1">
              <p className="eyebrow mb-1 !text-[#A8674F]">Progresso salvato</p>
              <p className="text-[#2A3229] leading-snug">
                Hai un Check-Up in corso per{" "}
                <strong className="text-[#323C36]">{savedProgress.company}</strong>
                {" "}— completato al <strong className="text-[#323C36]">{Math.round((savedProgress.count / totalQ) * 100)}%</strong>
              </p>
            </div>
            <div className="flex gap-3 shrink-0">
              <button
                data-testid="resume-yes"
                type="button"
                onClick={onResume}
                className="btn-primary !py-2.5 !px-5 text-[11px]"
              >
                Riprendi
              </button>
              <button
                data-testid="resume-no"
                type="button"
                onClick={() => {
                  try { localStorage.removeItem(storageKey); } catch(_) {}
                  window.location.reload();
                }}
                className="text-xs uppercase tracking-[0.18em] font-semibold text-[#5A6058] hover:text-[#323C36]"
              >
                Nuovo
              </button>
            </div>
          </div>
        )}

        <form onSubmit={submit} data-testid="quiz-form-box" className="bg-white border border-[#323C36]/12 shadow-[0_24px_60px_-24px_rgba(42,50,41,0.28)] p-6 sm:p-10 space-y-5 sm:space-y-6">
          <div>
            <label className="text-xs uppercase tracking-widest text-[#5A6058] mb-2 sm:mb-3 block">Nome Azienda *</label>
            <input required data-testid="w-company" className="field-input" placeholder="es. ACME Srl"
                   value={company} onChange={e=>setCompany(e.target.value)} autoFocus/>
          </div>
          <button data-testid="w-start" type="submit" className="btn-accent w-full !py-3.5 sm:!py-4">
            Ottieni la tua diagnosi gratuita <ArrowRight size={16}/>
          </button>
          <p data-testid="privacy-note" className="flex items-center justify-center gap-2 text-xs text-[#5A6058]">
            <Lock size={12}/> Dati protetti. Nessuno spam.
          </p>
        </form>

        {/* SOCIAL PROOF */}
        <div data-testid="social-proof" className="mt-6 sm:mt-8 flex flex-col items-center gap-2 text-center">
          <div className="flex items-center gap-1" aria-hidden="true">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={15} className="fill-[#E8A050] text-[#E8A050]" />
            ))}
          </div>
          <p className="text-sm text-[#5A6058] max-w-md">
            Oltre <strong className="text-[#2A3229]">20 aziende</strong> hanno già individuato i propri colli di bottiglia operativi con il Metodo A.L.L.I.N.E.A.
          </p>
        </div>
      </div>
    </section>
  );
}

function ClientInfoForm({ company, onSubmit, submitting }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    role: "", collaborators: "",
    privacy: false, newsletter: false,
  });
  const [err, setErr] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.role || !form.collaborators) {
      setErr("Compila tutti i campi obbligatori."); return;
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      setErr("L'indirizzo email non è valido. Controlla e riprova."); return;
    }
    if (!PHONE_RE.test(form.phone.trim())) {
      setErr("Il numero di telefono non è valido. Inserisci almeno 9 cifre (prefisso opzionale)."); return;
    }
    if (!form.privacy) {
      setErr("Devi accettare la Privacy Policy per procedere."); return;
    }
    setErr("");
    onSubmit({
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      company,
      role: form.role,
      collaborators: form.collaborators,
      privacy_accepted: form.privacy,
      newsletter_opt_in: form.newsletter,
    });
  };

  return (
    <section className="section-pad">
      <div className="max-w-2xl mx-auto px-6" data-testid="quiz-final-form">
        <div className="text-center mb-10">
          <p className="eyebrow mb-3">Ultimo passaggio</p>
          <h1 className="font-display text-3xl sm:text-4xl text-[#2A3229] leading-tight mb-4">
            Analisi completata con successo
          </h1>
          <p className="text-[#5A6058] leading-relaxed">
            Dati di configurazione dell'Analisi Aziendale per ricevere il tuo report personalizzato entro 24 ore.
          </p>
        </div>

        <form onSubmit={submit} className="bg-white border border-[#323C36]/12 p-8 sm:p-10 space-y-6">
          <div>
            <label className="text-xs uppercase tracking-widest text-[#5A6058] mb-2 block">Nome e Cognome *</label>
            <input required data-testid="ci-name" className="field-input" placeholder="es. Roberta Castagna"
                   value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#5A6058] mb-2 block">Email *</label>
            <input required type="email" data-testid="ci-email" className="field-input"
                   placeholder="es. nome@azienda.it" inputMode="email" autoComplete="email"
                   value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#5A6058] mb-2 block">Il tuo ruolo in azienda *</label>
            <select required data-testid="ci-role" className="field-input"
                    value={form.role} onChange={e=>setForm({...form,role:e.target.value})}>
              <option value="">Seleziona</option>
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#5A6058] mb-2 block">Quante persone compongono il tuo Team attualmente? *</label>
            <select required data-testid="ci-collab" className="field-input"
                    value={form.collaborators} onChange={e=>setForm({...form,collaborators:e.target.value})}>
              <option value="">Seleziona</option>
              {COLLABORATORS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#5A6058] mb-2 block">Numero di Telefono *</label>
            <input required type="tel" data-testid="ci-phone" className="field-input"
                   placeholder="es. +39 347 506 1407" inputMode="tel" autoComplete="tel"
                   minLength={9} maxLength={22}
                   value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})}/>
          </div>

          <div className="pt-4 border-t border-[#323C36]/10 space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input required type="checkbox" data-testid="ci-privacy"
                     className="mt-1 w-4 h-4 accent-[#323C36] shrink-0"
                     checked={form.privacy} onChange={e=>setForm({...form,privacy:e.target.checked})}/>
              <span className="text-sm text-[#2A3229] leading-relaxed">
                Accetto la <a href="#" className="underline">Privacy Policy</a> per il trattamento dei miei dati finalizzato all'elaborazione del Report. <span className="text-[#A8674F]">*</span>
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" data-testid="ci-newsletter"
                     className="mt-1 w-4 h-4 accent-[#323C36] shrink-0"
                     checked={form.newsletter} onChange={e=>setForm({...form,newsletter:e.target.checked})}/>
              <span className="text-sm text-[#5A6058] leading-relaxed">
                Desidero ricevere via email analisi di casi studio e strategie settimanali sui 7 pilastri del Metodo A.L.L.I.N.E.A.
              </span>
            </label>
          </div>

          {err && <p className="text-sm text-[#A8674F]" data-testid="ci-error">{err}</p>}

          <button data-testid="ci-submit" type="submit" disabled={submitting}
                  className="btn-accent w-full disabled:opacity-50">
            {submitting ? "Generazione in corso…" : "Genera la mia Diagnosi Aziendale ➔"}
          </button>
        </form>
      </div>
    </section>
  );
}

export default function Quiz() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  // /checkup = versione rapida 9 domande (traffico Meta Ads)
  // /checkup-completo = versione completa 42 domande (link manuale per lead caldi, noindex)
  const mode = pathname === "/checkup-completo" ? "full" : "quick";
  const totalQ = mode === "full" ? 42 : 9;
  const STORAGE_KEY = mode === "full" ? "rc_checkup42_progress" : "rc_checkup_progress";
  const [questions, setQuestions] = useState([]);
  const [pillars, setPillars] = useState([]);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [phase, setPhase] = useState("welcome"); // welcome | questions | client | done
  const [company, setCompany] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const advanceTimerRef = useRef(null);
  const [advancing, setAdvancing] = useState(false);
  const [savedProgress, setSavedProgress] = useState(null);
  const hydratedRef = useRef(false);
  const [celebrating, setCelebrating] = useState(null); // { letter, name } quando un pilastro è appena completato

  useEffect(() => {
    api.get("/quiz/questions", { params: { mode } }).then(r => {
      setQuestions(r.data.questions);
      setPillars(r.data.pillars || []);
    }).catch(()=>{});
  }, [mode]);

  // Rileva progresso salvato all'apertura della pagina (una sola volta).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const p = JSON.parse(raw);
      if (!p || !p.company || !p.answers) return;
      setSavedProgress({
        company: p.company,
        step: typeof p.step === "number" ? p.step : 0,
        answers: p.answers,
        count: Object.keys(p.answers || {}).length,
      });
    } catch (_) { /* storage bloccato o JSON invalido → ignora */ }
  }, []);

  // Autosave: dopo ogni cambiamento di risposta o step, persistiamo lo stato
  // per l'azienda corrente. Non salviamo nulla in phase="welcome" (nessun dato).
  useEffect(() => {
    if (phase === "welcome" || phase === "done") return;
    if (!company) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        company,
        answers,
        step,
        savedAt: new Date().toISOString(),
      }));
    } catch (_) {}
  }, [answers, step, company, phase]);

  // Reset completo del Check-Up quando l'utente clicca "CHECK-UP AZIENDALE"
  // dall'header mentre è già su /checkup (fix del bottone bloccato dopo errore).
  useEffect(() => {
    const reset = () => {
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
      setAnswers({});
      setStep(0);
      setCompany("");
      setSubmitting(false);
      setAdvancing(false);
      setPhase("welcome");
      sessionStorage.removeItem("rc_lead_fired");
      // Il reset esplicito dall'header cancella anche il progresso salvato
      try { localStorage.removeItem(STORAGE_KEY); } catch(_) {}
      setSavedProgress(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    };
    window.addEventListener("checkup:reset", reset);
    return () => window.removeEventListener("checkup:reset", reset);
  }, []);

  // Pulizia timer al unmount
  useEffect(() => () => {
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
  }, []);

  const startFromWelcome = (companyName) => {
    // Nuovo Check-Up: se c'era un progresso di altra azienda lo scartiamo
    if (savedProgress && savedProgress.company !== companyName) {
      try { localStorage.removeItem(STORAGE_KEY); } catch(_) {}
      setSavedProgress(null);
      setAnswers({});
      setStep(0);
    }
    setCompany(companyName);
    setPhase("questions");
    // Tracking abbandoni: registra l'inizio del Check-Up (fire and forget)
    api.post("/quiz/start", { company: companyName, mode }).catch(() => {});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resumeFromSaved = () => {
    if (!savedProgress) return;
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    setCompany(savedProgress.company);
    setAnswers(savedProgress.answers || {});
    // Se ci sono risposte, riprendi dalla prima domanda senza risposta,
    // altrimenti dallo step salvato.
    if (questions.length) {
      const missingIdx = questions.findIndex(q => (savedProgress.answers || {})[q.id] === undefined);
      setStep(missingIdx === -1 ? questions.length - 1 : missingIdx);
    } else {
      setStep(savedProgress.step || 0);
    }
    setPhase("questions");
    toast.success(`Progresso ripristinato per ${savedProgress.company}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const currentQ = questions[step];
  const answered = currentQ && answers[currentQ.id];
  const isLast = step === questions.length - 1;
  const canFinish = isLast && !!answered;

  const setAnswer = (q, opt) => {
    // Reset timer precedente (fix race condition: se l'utente cambia idea e clicca
    // un'altra opzione sulla stessa domanda, non deve triggerare doppio setStep).
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);

    // Aggiorna la risposta in modo sincrono nel functional update
    const newAnswers = {...answers, [q.id]: opt.value};
    setAnswers(newAnswers);
    setAdvancing(true);

    // Micro-celebrazione: se questa risposta completa un pilastro (tutte le
    // domande del pilastro corrente hanno una risposta), mostra il badge.
    const currentPillarKey = q.pillar_key;
    const pillarQuestions = questions.filter(qq => qq.pillar_key === currentPillarKey);
    const pillarAnswered = pillarQuestions.every(qq => newAnswers[qq.id] !== undefined);
    if (pillarAnswered) {
      const p = pillars.find(pl => pl.key === currentPillarKey);
      if (p && step < questions.length - 1) {
        setCelebrating({ letter: p.letter, name: p.name });
        playPillarTick();
        setTimeout(() => setCelebrating(null), 1400);
      }
    }

    if (step < questions.length - 1) {
      advanceTimerRef.current = setTimeout(() => {
        setStep(s => Math.min(s + 1, questions.length - 1));
        setAdvancing(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 320);
    } else {
      // Ultima domanda: prima di andare al form, verifichiamo che nessuna risposta
      // manchi (protezione totale contro race conditions). Se manca qualcosa,
      // riportiamo l'utente alla prima domanda incompleta.
      advanceTimerRef.current = setTimeout(() => {
        const missingIdx = questions.findIndex(qq => newAnswers[qq.id] === undefined);
        if (missingIdx !== -1) {
          setStep(missingIdx);
          toast.error("Manca una risposta — ti riporto alla domanda");
          window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          setPhase("client");
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
        setAdvancing(false);
      }, 320);
    }
  };
  const prev = () => { setStep(s => Math.max(0, s-1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const goToClient = () => {
    const missingIdx = questions.findIndex(q => !answers[q.id]);
    if (missingIdx !== -1) {
      setStep(missingIdx);
      toast.error("Manca una risposta — ti riporto alla domanda");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setPhase("client");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitAll = async (clientInfo) => {
    if (submitting) return;

    // Guardrail: se per qualunque motivo mancano risposte, non tentare la POST
    // (evita il 400 "Servono esattamente 42 risposte") e riportiamo l'utente
    // alla prima domanda mancante.
    const missingIdx = questions.findIndex(q => answers[q.id] === undefined);
    if (missingIdx !== -1) {
      toast.error("Ti riporto alla prima domanda senza risposta");
      setStep(missingIdx);
      setPhase("questions");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        client: clientInfo,
        answers: Object.entries(answers).map(([qid, score]) => ({ question_id: qid, score })),
      };
      await api.post("/quiz/submit", payload);
      // Submit riuscito → cancella il progresso salvato (Check-Up completato)
      try { localStorage.removeItem(STORAGE_KEY); } catch(_) {}
      setPhase("done");
      toast.success("Diagnosi generata!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore invio");
    } finally { setSubmitting(false); }
  };

  if (phase === "done") return <><QuizSEO mode={mode}/><ThankYou/></>;
  if (phase === "welcome") return (
    <>
      <QuizSEO mode={mode}/>
      <WelcomeScreen
        onStart={startFromWelcome}
        onResume={resumeFromSaved}
        savedProgress={savedProgress}
        mode={mode}
        storageKey={STORAGE_KEY}
        totalQ={totalQ}
      />
    </>
  );
  if (phase === "client") return <><QuizSEO mode={mode}/><ClientInfoForm company={company} onSubmit={submitAll} submitting={submitting}/></>;

  if (!questions.length) return <><QuizSEO mode={mode}/><div className="section-pad text-center text-[#5A6058]">Caricamento Check‑Up…</div></>;

  const currentPillar = pillars.find(p => p.key === currentQ.pillar_key);
  const progressBase = isLast && answered ? questions.length : (step + (answered ? 1 : 0));
  const progress = (progressBase / questions.length) * 100;

  return (
    <section className="section-pad">
      <QuizSEO mode={mode}/>
      <div className="max-w-3xl mx-auto px-6">
        <div className="mb-10">
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="eyebrow mb-2 !text-[10px]">Check‑Up A.L.L.I.N.E.A.</p>
              {currentPillar && (
                <div className="flex items-baseline gap-4">
                  <span className="font-display text-5xl sm:text-6xl text-[#A8674F] font-light leading-none">{currentPillar.letter}</span>
                  <span className="font-display text-lg sm:text-xl text-[#2A3229] leading-tight">{currentPillar.name}</span>
                </div>
              )}
            </div>
            <div className="text-right shrink-0">
              <p className="font-display text-3xl sm:text-4xl text-[#323C36] font-light leading-none tabular-nums">{Math.round(progress)}%</p>
              <p className="text-[10px] uppercase tracking-[0.24em] text-[#5A6058] mt-1">completato</p>
            </div>
          </div>

          <div className="h-[3px] bg-[#323C36]/10 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#A8674F] via-[#FAC075] to-[#323C36] transition-all duration-500 ease-out" style={{width: `${progress}%`}}/>
          </div>

          <div className="flex justify-between mt-4 px-1">
            {pillars.map((p) => {
              const pQuestions = questions.filter(q => q.pillar_key === p.key);
              const pAnswered = pQuestions.filter(q => answers[q.id]).length;
              const done = pAnswered === pQuestions.length && pQuestions.length > 0;
              const active = currentPillar && p.key === currentPillar.key;
              return (
                <div key={p.key} className="flex flex-col items-center gap-1.5" data-testid={`pillar-dot-${p.key}`}>
                  <div className={`w-2 h-2 rounded-full transition-all ${
                    done ? "bg-[#323C36] scale-110"
                      : active ? "bg-[#A8674F] scale-125 ring-2 ring-[#A8674F]/25 ring-offset-2 ring-offset-[#F6F3EF]"
                      : "bg-[#323C36]/20"
                  }`}/>
                  <span className={`text-[10px] font-semibold transition-colors ${
                    done ? "text-[#323C36]" : active ? "text-[#A8674F]" : "text-[#323C36]/25"
                  }`}>{p.letter}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="relative min-h-[420px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQ.id}
              data-testid={`q-${currentQ.id}`}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
            >
              <h2 className="font-display text-2xl sm:text-3xl text-[#2A3229] leading-tight mb-8">{currentQ.text}</h2>
              <div className="space-y-3">
                {currentQ.options.map(opt => {
                  const sel = answers[currentQ.id] === opt.value;
                  return (
                    <button
                      data-testid={`q-${currentQ.id}-opt-${opt.value}`}
                      key={opt.value}
                      onClick={() => setAnswer(currentQ, opt)}
                      disabled={advancing}
                      className={`group relative w-full text-left px-6 py-4 border transition-all duration-300 disabled:cursor-not-allowed ${
                        sel
                          ? 'border-[#323C36] bg-[#323C36] text-[#F6F3EF] scale-[1.005]'
                          : 'border-[#323C36]/15 bg-white hover:border-[#323C36] hover:translate-x-1'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold shrink-0 transition-all ${
                          sel ? 'bg-[#FAC075] text-[#2A3229]' : 'bg-[#EDE7DD] text-[#323C36] group-hover:bg-[#FAC075]/50'
                        }`}>
                          {String.fromCharCode(64 + opt.value)}
                        </span>
                        <span className="flex-1">{opt.label}</span>
                        {sel && <span className="text-[#FAC075] text-lg leading-none">✓</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Micro-celebrazione al completamento di un pilastro */}
          <AnimatePresence>
            {celebrating && (
              <motion.div
                data-testid="pillar-celebration"
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.05 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="pointer-events-none absolute inset-0 flex items-center justify-center z-10"
              >
                <div className="bg-[#2A3229] text-[#F6F3EF] px-8 py-5 shadow-2xl flex items-center gap-5">
                  <motion.span
                    initial={{ scale: 0.6, rotate: -10 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="font-display text-5xl text-[#FAC075] font-light leading-none"
                  >
                    {celebrating.letter}
                  </motion.span>
                  <div>
                    <p className="eyebrow !text-[#FAC075] mb-1">Pilastro completato</p>
                    <p className="font-display text-lg leading-tight">{celebrating.name}</p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="mt-12 flex justify-between items-center">
          <button data-testid="q-prev" onClick={prev} disabled={step===0}
                  className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] font-semibold text-[#5A6058] hover:text-[#323C36] disabled:opacity-25 disabled:cursor-not-allowed transition-colors">
            <ArrowLeft size={14}/> Indietro
          </button>

          {isLast && (
            <button data-testid="q-submit" onClick={goToClient} disabled={!canFinish}
                    className="btn-accent disabled:opacity-50">
              Prosegui <ArrowRight size={16}/>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
